import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, FileText, Sparkles, Check } from '../components/Icons.jsx';

const promptSets = [
  {
    theme: 'Truth and belief',
    type: 'A',
    quotes: [
      '"Believe nothing merely because you have been told it." — Buddha',
      '"The most strenuous of efforts cannot save the truth from those who do not wish to find it." — Anonymous',
      '"It is the customary fate of new truths to begin as heresies and end as superstitions." — T. H. Huxley',
      '"We have art so that we shall not die of reality." — Friedrich Nietzsche'
    ]
  },
  {
    theme: 'Progress',
    type: 'A',
    quotes: [
      '"The reasonable man adapts himself to the world; the unreasonable one persists in trying to adapt the world to himself. Therefore all progress depends on the unreasonable man." — George Bernard Shaw',
      '"Progress, far from consisting in change, depends on retentiveness." — George Santayana',
      '"It is not necessary to change. Survival is not mandatory." — W. Edwards Deming',
      '"Civilisation advances by extending the number of important operations which we can perform without thinking about them." — Alfred North Whitehead'
    ]
  },
  {
    theme: 'Solitude and society',
    type: 'B',
    quotes: [
      '"All of humanity\'s problems stem from man\'s inability to sit quietly in a room alone." — Blaise Pascal',
      '"Loneliness is the poverty of self; solitude is the richness of self." — May Sarton',
      '"Hell is other people." — Jean-Paul Sartre',
      '"The eternal silence of these infinite spaces frightens me." — Blaise Pascal'
    ]
  },
  {
    theme: 'Memory',
    type: 'B',
    quotes: [
      '"We are all of us the heroes of our own private struggle, and forget that everyone else is fighting too." — Anonymous',
      '"Memory believes before knowing remembers." — William Faulkner',
      '"The past is a foreign country: they do things differently there." — L. P. Hartley',
      '"Forgetting is part of what makes us human." — Anonymous'
    ]
  },
  {
    theme: 'Risk and certainty',
    type: 'A',
    quotes: [
      '"He who is not courageous enough to take risks will accomplish nothing in life." — Muhammad Ali',
      '"To know one\'s ignorance is the best part of knowledge." — Lao Tzu',
      '"In the midst of chaos, there is also opportunity." — Sun Tzu',
      '"Uncertainty is the only certainty there is." — John Allen Paulos'
    ]
  }
];

const structure = [
  { label: 'Hook + thesis', target: '~40s', body: 'Open with a specific image, anecdote or arresting claim. End paragraph 1 with a single clear thesis sentence — the line a marker could circle if asked "what is your argument?"' },
  { label: 'Body 1 — strongest support', target: '~7 min', body: 'Lead with your most concrete piece of evidence or example. Real-world cases beat abstractions. Connect each example back to the thesis explicitly.' },
  { label: 'Body 2 — second angle', target: '~7 min', body: 'Different sphere from Body 1 (e.g. if Body 1 was personal, make Body 2 political or historical). The contrast itself shows breadth.' },
  { label: 'Body 3 — counterpoint + nuance', target: '~7 min', body: 'Acknowledge a tension or a case where your thesis fails. Then resolve or qualify it. This is the paragraph that distinguishes a high score.' },
  { label: 'Synthesis', target: '~2 min', body: 'Not a summary. Tie the threads together into one new observation. Markers want a closing line that reframes the question.' }
];

const TOTAL_SECONDS = 30 * 60;

export default function GamsatEssay() {
  const [stage, setStage] = useState('intro');
  const [promptIdx, setPromptIdx] = useState(0);
  const [showStructure, setShowStructure] = useState(true);
  const [text, setText] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);
  const textareaRef = useRef(null);

  const prompt = promptSets[promptIdx];

  useEffect(() => {
    if (stage !== 'writing' || paused) return;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          setStage('done');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [stage, paused]);

  const start = (idx) => {
    setPromptIdx(idx ?? promptIdx);
    setText('');
    setSecondsLeft(TOTAL_SECONDS);
    setPaused(false);
    setStage('writing');
    setTimeout(() => textareaRef.current?.focus(), 50);
  };
  const newPrompt = () => {
    const next = (promptIdx + 1 + Math.floor(Math.random() * (promptSets.length - 1))) % promptSets.length;
    setPromptIdx(next);
  };
  const finish = () => setStage('done');
  const restart = () => { setStage('intro'); setText(''); setSecondsLeft(TOTAL_SECONDS); };

  const wordCount = useMemo(() => text.trim() ? text.trim().split(/\s+/).length : 0, [text]);
  const charCount = text.length;
  const timeStr = `${Math.floor(secondsLeft / 60)}:${(secondsLeft % 60).toString().padStart(2, '0')}`;
  const elapsedPct = ((TOTAL_SECONDS - secondsLeft) / TOTAL_SECONDS) * 100;
  const timeColour = secondsLeft < 5 * 60 ? '#b91c1c' : secondsLeft < 10 * 60 ? '#b45309' : 'var(--text-2)';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)', background: '#fff', position: 'sticky', top: 0, zIndex: 10 }}>
        <Link to="/gamsat" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--text-2)', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>
          <ArrowLeft width="16" height="16" />Back to GAMSAT guide
        </Link>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-deep)' }}>Section 2 · Essay sprint</div>
        {stage === 'writing' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 14 }}>
            <span style={{ color: timeColour, fontWeight: 700, fontFamily: 'monospace', fontSize: 18 }}>{timeStr}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{wordCount} words</span>
            <button onClick={() => setPaused(!paused)} style={btnSecondary}>{paused ? 'Resume' : 'Pause'}</button>
            <button onClick={finish} style={btnPrimary}>Finish</button>
          </div>
        ) : <div style={{ width: 1 }} />}
      </header>

      {stage === 'writing' && (
        <div style={{ height: 3, background: 'var(--card-soft)' }}>
          <div style={{ width: `${elapsedPct}%`, height: '100%', background: `linear-gradient(90deg, var(--brand-purple), ${secondsLeft < 5 * 60 ? '#b91c1c' : 'var(--brand-teal)'})`, transition: 'width 600ms linear' }} />
        </div>
      )}

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(20px, 4vw, 36px)' }}>
        {stage === 'intro' && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Free GAMSAT tool</div>
            <h1 style={{ margin: '6px 0 12px', fontSize: 'clamp(26px, 5vw, 36px)' }}>Section 2 essay practice — 30-minute sprint</h1>
            <p style={{ color: 'var(--text-2)', fontSize: 16, lineHeight: 1.6, maxWidth: 720 }}>One quote-set, one 30-minute timer, one essay. Mirrors the real GAMSAT pacing of 30 minutes per essay. Pick a theme below to begin — or randomise it for re-use.</p>

            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {promptSets.map((p, i) => (
                <button key={p.theme} onClick={() => start(i)} style={{
                  textAlign: 'left', padding: '18px 22px', background: '#fff',
                  border: '1px solid var(--border)', borderRadius: 14, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
                  transition: 'border-color 150ms ease, box-shadow 150ms ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--brand-purple)'; e.currentTarget.style.boxShadow = 'var(--shadow-card-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Task {p.type} · {p.theme}</div>
                    <div style={{ fontSize: 15, color: 'var(--text-2)', marginTop: 4 }}>{p.quotes.length} quotes · 30-min timer</div>
                  </div>
                  <ArrowRight width="18" height="18" />
                </button>
              ))}
            </div>
          </>
        )}

        {stage === 'writing' && (
          <div style={{ display: 'grid', gridTemplateColumns: showStructure ? '1fr 320px' : '1fr', gap: 20 }} className="essay-shell">
            <div>
              <div className="card" style={{ marginBottom: 16, background: 'var(--card-soft)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Task {prompt.type} · {prompt.theme}</div>
                <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--text-muted)' }}>Consider these quotes and write an essay developed from any one or more of them.</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {prompt.quotes.map((q, i) => (
                    <li key={i} style={{ fontSize: 14.5, lineHeight: 1.5, fontStyle: 'italic', color: 'var(--text)' }}>{q}</li>
                  ))}
                </ul>
              </div>

              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Start writing…"
                style={{
                  width: '100%', minHeight: 'min(60vh, 480px)', padding: '20px 22px',
                  fontSize: 16, lineHeight: 1.7, fontFamily: 'Georgia, serif',
                  border: '1px solid var(--border)', borderRadius: 14, resize: 'vertical',
                  background: '#fff', outline: 'none'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = 'var(--brand-purple)'}
                onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
              />
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--text-muted)' }}>
                <span>{charCount} characters · {wordCount} words</span>
                <button onClick={() => setShowStructure(!showStructure)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', fontWeight: 600, fontSize: 13 }}>
                  {showStructure ? 'Hide structure prompts' : 'Show structure prompts'}
                </button>
              </div>
            </div>

            {showStructure && (
              <aside style={{ background: 'var(--card-soft)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', alignSelf: 'start' }} className="essay-aside">
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Suggested structure</div>
                {structure.map((s, i) => (
                  <div key={s.label} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                      <strong style={{ fontSize: 13.5 }}>{i + 1}. {s.label}</strong>
                      <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 600 }}>{s.target}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>{s.body}</p>
                  </div>
                ))}
              </aside>
            )}
          </div>
        )}

        {stage === 'done' && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Essay complete</div>
            <h1 style={{ margin: '6px 0 12px', fontSize: 'clamp(28px, 5vw, 40px)', display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
              {wordCount} <span style={{ color: 'var(--text-muted)', fontSize: 22, fontWeight: 500 }}>words in {Math.floor((TOTAL_SECONDS - secondsLeft) / 60)} min</span>
            </h1>

            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Quick self-marking checklist</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, color: 'var(--text-2)', fontSize: 14.5, lineHeight: 1.5 }}>
                {[
                  'Did your introduction end with a single clear thesis sentence?',
                  'Did Body 3 acknowledge a counterpoint or limitation of your argument?',
                  'Did the synthesis paragraph add a new observation rather than restate?',
                  'Are at least two distinct domains (personal / political / historical / cultural / scientific) represented in your examples?',
                  'Word count between 450 and 650? Below 350 is usually too thin; above 750 wastes time you needed for the second essay.'
                ].map((q) => (
                  <li key={q} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}><span style={{ color: 'var(--brand-purple)', marginTop: 2 }}>▸</span>{q}</li>
                ))}
              </ul>
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Your essay</h3>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 15.5, lineHeight: 1.7, color: 'var(--text)', whiteSpace: 'pre-wrap', padding: '14px 16px', background: 'var(--card-soft)', borderRadius: 10 }}>
                {text || <em style={{ color: 'var(--text-muted)' }}>No text written.</em>}
              </div>
              {text && (
                <button onClick={() => navigator.clipboard?.writeText(text)} className="btn btn-secondary" style={{ marginTop: 12 }}>
                  Copy essay <FileText width="14" height="14" />
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
              <button onClick={() => start(promptIdx)} className="btn btn-primary">Run again, same prompt <ArrowRight width="14" height="14" /></button>
              <button onClick={() => { newPrompt(); restart(); }} className="btn btn-secondary"><Sparkles width="14" height="14" />New prompt</button>
              <Link to="/medi" className="btn btn-secondary">Ask Ash to critique it</Link>
              <Link to="/gamsat" className="btn btn-secondary">Back to GAMSAT guide</Link>
            </div>

            <section className="parents-cta" style={{ marginTop: 12 }}>
              <div>
                <h2>Want a real marker on your essay?</h2>
                <p>Medic Mind GAMSAT tutors review Section 2 essays with the rubric ACER uses. Detailed feedback on thesis clarity, evidence depth and structural choices — what AI can't reliably score.</p>
              </div>
              <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book essay feedback <ArrowRight width="16" height="16" /></a>
            </section>
          </>
        )}
      </main>

      <style>{`
        @media (max-width: 720px) {
          .essay-shell { grid-template-columns: 1fr !important; }
          .essay-aside { order: -1; }
        }
      `}</style>
    </div>
  );
}

const btnBase = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', fontSize: 13, fontWeight: 600,
  borderRadius: 10, cursor: 'pointer', border: '1px solid transparent'
};
const btnPrimary = { ...btnBase, background: 'var(--brand-deep)', color: '#fff' };
const btnSecondary = { ...btnBase, background: '#fff', border: '1px solid var(--border)', color: 'var(--text-2)' };
